import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  AttachCouponDocument,
  AttachGroupProfileDocument,
  LoginUserProfileDocument,
  ServicesInput,
} from "@graphql/api";
import { processJWT } from "@lib/auth/jwt";

type ProfileType = "CHARTER" | "COUPON" | "SALES" | "USER" | "GROUP";

interface LoginResult {
  success: boolean;
  error?: string;
  errorType?: string;
  token?: string;
}


// Handle user profile login
async function loginUser(
  client: ApolloClient<NormalizedCacheObject>,
  emailAddress: string,
  password: string,
): Promise<LoginResult> {
  const userResult = await client.mutate({
    mutation: LoginUserProfileDocument,
    variables: { emailAddress, password: password || "" },
  });

  const response = userResult.data?.loginUserProfile;
  if (!response?.success) {
    return {
      success: false,
      error: response?.message || response?.errorType || "Login failed",
    };
  }

  return {
    success: true,
    token: response.auth?.token || undefined,
  };
}

// Handle group profile attachment
async function attachGroup(
  client: ApolloClient<NormalizedCacheObject>,
  id: string,
  password?: string,
): Promise<LoginResult> {
  const groupResult = await client.mutate({
    mutation: AttachGroupProfileDocument,
    variables: { id, password },
  });

  const response = groupResult.data?.attachGroupProfile;
  if (!response?.success) {
    // For PASSWORD_NEEDED, always return the errorType
    if (response?.errorType === "PASSWORD_NEEDED") {
      return {
        success: false,
        error: "PASSWORD_NEEDED",
      };
    }
    return {
      success: false,
      error: response?.message || response?.errorType || "Invalid group code or password",
    };
  }

  return {
    success: true,
    token: response.auth?.token || undefined,
  };
}

// Handle coupon attachment.
// `legs` lets nAPI run a trip-aware services validation with the proposed
// coupon profile applied — Bridge will reject date-restricted coupons (and
// other trip-specific failures) up front, so the coupon never makes it into
// the JWT. The displayMessage from Bridge is surfaced via `response.message`.
async function attachCoupon(
  client: ApolloClient<NormalizedCacheObject>,
  couponCode: string,
  legs?: ServicesInput[],
): Promise<LoginResult> {
  const couponResult = await client.mutate({
    mutation: AttachCouponDocument,
    variables: { couponCode, legs },
  });

  const response = couponResult.data?.attachCoupon;
  if (!response?.success) {
    return {
      success: false,
      error: response?.message || response?.errorType || "Invalid coupon code",
      errorType: response?.errorType || undefined,
    };
  }

  return {
    success: true,
    token: response.auth?.token || undefined,
  };
}

export default async function loginProfile({
  id,
  password,
  type,
  legs,
}: {
  id: string;
  password?: string;
  type: ProfileType;
  // Only used for COUPON: enables nAPI to validate the coupon against the
  // current trip before issuing a JWT with the coupon attached.
  legs?: ServicesInput[];
}): Promise<{
  actualType?: string;
  error?: string;
  errorType?: string;
  success?: boolean;
}> {
  const client = window.apolloClient;

  let result: LoginResult;

  try {
    switch (type) {
      case "USER":
        result = await loginUser(client, id, password || "");
        break;

      case "GROUP":
        result = await attachGroup(client, id, password);
        break;

      case "COUPON":
        result = await attachCoupon(client, id, legs);
        break;

      case "CHARTER":
      case "SALES":
        // CHARTER and SALES profile types are not used directly in the BE app.
        // They are only set programmatically through PID processing in login_client_center.
        return { error: `Profile type ${type} is not supported for direct login`, success: false };

      default:
        return { error: `Unsupported profile type: ${type}`, success: false };
    }
  } catch (error) {
    console.error("Login error:", error);
    return {
      error: error instanceof Error ? error.message : "Login failed",
      success: false,
    };
  }

  if (!result.success)
    return { error: result.error, errorType: result.errorType, success: false };

  // Process the JWT token and update session
  let actualType: string = type;
  if (result.token) {
    const profiles = await processJWT(result.token);
    // Determine actual type from what's in the JWT (e.g., GROUP login might return COUPON)
    if (type === "GROUP" && profiles?.coupon && !profiles?.group) actualType = "COUPON";
  }

  return { success: true, actualType };
}

